import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// --- Autenticazione Studio (token Bearer in localStorage) ---
const TOKEN_KEY = "studio_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      window.dispatchEvent(new Event("studio-unauthorized"));
    }
    return Promise.reject(err);
  },
);

export const login = (password) => api.post("/auth/login", { password }).then((r) => r.data);
export const checkAuth = () => api.get("/auth/me").then((r) => r.data);

export const generateScript = (payload) => api.post("/scripts/generate", payload).then((r) => r.data);
export const generateLeadMagnet = (payload) => api.post("/lead-magnet/generate", payload).then((r) => r.data);
export const classifyContent = (testo) => api.post("/scripts/classify", { testo }).then((r) => r.data);
export const generateIdeas = (payload) => api.post("/ideas/generate", payload).then((r) => r.data);
export const getDailyIdeas = (refresh = false) => api.get("/ideas/daily", { params: { refresh } }).then((r) => r.data);
export const getDailyIdeasByDate = (date) => api.get("/ideas/daily", { params: { date } }).then((r) => r.data);
export const getIdeaDates = () => api.get("/ideas/dates").then((r) => r.data);
export const getFreshNews = (refresh = false) => api.get("/news/fresh", { params: { refresh } }).then((r) => r.data);
export const reactToNews = (payload) => api.post("/news/react", payload).then((r) => r.data);
export const saveNews = (payload) => api.post("/news/saved", payload).then((r) => r.data);
export const listSavedNews = () => api.get("/news/saved").then((r) => r.data);
export const deleteSavedNews = (id) => api.delete(`/news/saved/${id}`).then((r) => r.data);
export const setNewsTema = (title, tema) => api.post("/news/tema", { title, tema }).then((r) => r.data);
export const getFunnelStats = () => api.get("/stats/funnel").then((r) => r.data);
export const dmReply = (payload) => api.post("/dm/reply", payload).then((r) => r.data);
export const saveIdea = (payload) => api.post("/ideas/saved", payload).then((r) => r.data);
export const listSavedIdeas = () => api.get("/ideas/saved").then((r) => r.data);
export const deleteSavedIdea = (id) => api.delete(`/ideas/saved/${id}`).then((r) => r.data);
export const saveScript = (payload) => api.post("/scripts", payload).then((r) => r.data);
export const listScripts = (categoria) =>
  api.get("/scripts", { params: categoria && categoria !== "Tutte" ? { categoria } : {} }).then((r) => r.data);
export const deleteScript = (id) => api.delete(`/scripts/${id}`).then((r) => r.data);
export const updateScript = (id, payload) => api.put(`/scripts/${id}`, payload).then((r) => r.data);
export const generateStorie = (id) => api.post(`/scripts/${id}/storie`).then((r) => r.data);

export const getPlan = (anno, mese) => api.get("/plan", { params: { anno, mese } }).then((r) => r.data);
export const createPlanItem = (payload) => api.post("/plan", payload).then((r) => r.data);
export const updatePlanItem = (id, payload) => api.put(`/plan/${id}`, payload).then((r) => r.data);
export const deletePlanItem = (id) => api.delete(`/plan/${id}`).then((r) => r.data);

export const getStrategyProfile = () => api.get("/strategy/profile").then((r) => r.data);
export const updateStrategyProfile = (payload) => api.put("/strategy/profile", payload).then((r) => r.data);
export const getStrategyWeek = (start) => api.get("/strategy/week", { params: start ? { start } : {} }).then((r) => r.data);
export const patchStrategySlot = (start, giorno, payload) => api.patch(`/strategy/week/${start}/slot/${giorno}`, payload).then((r) => r.data);
export const strategySlotToPlan = (start, giorno) => api.post(`/strategy/week/${start}/slot/${giorno}/to-plan`).then((r) => r.data);
export const getStrategyBridge = () => api.get("/strategy/bridge").then((r) => r.data);
export const runStrategyDiagnosis = () => api.post("/strategy/diagnosis/run").then((r) => r.data);
export const getStrategyDiagnosisStatus = () => api.get("/strategy/diagnosis/status").then((r) => r.data);
export const getStrategyDiagnosis = (id) => api.get("/strategy/diagnosis", { params: id ? { id } : {} }).then((r) => r.data);
export const applyStrategyDiagnosis = (id) => api.post(`/strategy/diagnosis/${id}/apply`).then((r) => r.data);

export const igStatus = () => api.get("/instagram/status").then((r) => r.data);
export const igAuthStart = () => api.get("/instagram/auth/start").then((r) => r.data);
export const igSync = () => api.post("/instagram/sync").then((r) => r.data);
export const igSettings = () => api.get("/instagram/settings").then((r) => r.data);
export const igReels = (sort = "views", opts = {}) => {
  const params = { sort };
  if (opts.since) params.since = opts.since;
  if (opts.months != null) params.months = opts.months;
  return api.get("/instagram/reels", { params }).then((r) => r.data);
};
export const igGrowth = () => api.get("/instagram/growth").then((r) => r.data);
export const igTiming = (opts = {}) => {
  const params = {};
  if (opts.since) params.since = opts.since;
  if (opts.months != null) params.months = opts.months;
  return api.get("/instagram/timing", { params }).then((r) => r.data);
};
export const igDemographics = (refresh = false) =>
  api.get("/instagram/demographics", { params: refresh ? { refresh: true } : {} }).then((r) => r.data);
export const igPatterns = (opts = {}) => {
  const params = {};
  if (opts.since) params.since = opts.since;
  if (opts.months != null) params.months = opts.months;
  return api.get("/instagram/patterns", { params }).then((r) => r.data);
};
export const igPillars = (opts = {}) => {
  const params = {};
  if (opts.since) params.since = opts.since;
  if (opts.months != null) params.months = opts.months;
  return api.get("/instagram/pillars", { params }).then((r) => r.data);
};
export const igGetReelAnalysis = (mediaId) =>
  api.get("/instagram/reel/analysis", { params: { media_id: mediaId } }).then((r) => r.data);
export const igAnalyzeReel = (mediaId, force = false) =>
  api.post("/instagram/reel/analyze", { media_id: mediaId, force }).then((r) => r.data);
export const igBulkAnalyze = (opts = {}) =>
  api.post("/instagram/reels/analyze-all", opts).then((r) => r.data);
export const igBulkStatus = () =>
  api.get("/instagram/reels/analyze-all/status").then((r) => r.data);
export const igAnalyzedIds = () =>
  api.get("/instagram/reels/analyzed-ids").then((r) => r.data.ids || []);
export const igGetSuggestions = () =>
  api.get("/instagram/suggestions").then((r) => r.data);
export const igGenerateSuggestions = () =>
  api.post("/instagram/suggestions/generate", {}).then((r) => r.data);
export const igDisconnect = () => api.post("/instagram/disconnect").then((r) => r.data);
export const igAnalyze = (opts = {}) => {
  const params = {};
  if (opts.since) params.since = opts.since;
  if (opts.months != null) params.months = opts.months;
  return api.post("/instagram/ai/analyze", null, { params }).then((r) => r.data);
};
export const igGetAnalysis = () => api.get("/instagram/ai/analyze").then((r) => r.data);
export const igSaveIdea = (payload) => api.post("/instagram/ai/save-idea", payload).then((r) => r.data);
export const igGetSavedIdeas = () => api.get("/instagram/ai/saved-ideas").then((r) => r.data);
export const igDeleteSavedIdea = (id) => api.delete(`/ideas/saved/${id}`).then((r) => r.data);

export const submitWorkshopLead = (payload) => api.post("/workshop/lead", payload).then((r) => r.data);
export const trackWorkshopEvent = (payload) => api.post("/workshop/track", payload).then((r) => r.data).catch(() => null);
export const getWorkshopStats = (days = 14) => api.get("/workshop/stats", { params: { days } }).then((r) => r.data);
export const getWorkshopLeads = () => api.get("/workshop/leads").then((r) => r.data);
export const resendWorkshopLead = (id) => api.post(`/workshop/leads/${id}/resend`).then((r) => r.data);
export const getWorkshopStatus = () => api.get("/workshop/status").then((r) => r.data);
export const updateWorkshopStatus = (payload) => api.put("/workshop/status", payload).then((r) => r.data);
export const submitWaitlist = (payload) => api.post("/workshop/waitlist", payload).then((r) => r.data);

// --- Semaforo Economico Familiare (lead magnet pubblico) ---
export const getSemaforoConfig = () => api.get("/semaforo/config").then((r) => r.data);
export const submitSemaforoResult = (payload) => api.post("/semaforo/result", payload).then((r) => r.data);
export const submitSemaforoLead = (payload) => api.post("/semaforo/lead", payload).then((r) => r.data);
export const trackSemaforoEvent = (payload) => api.post("/semaforo/track", payload).then((r) => r.data).catch(() => null);
export const getSemaforoStats = () => api.get("/semaforo/stats").then((r) => r.data);
export const getSemaforoLeads = () => api.get("/semaforo/leads").then((r) => r.data);
export const resendSemaforoLead = (id) => api.post(`/semaforo/leads/${id}/resend`).then((r) => r.data);
export const semaforoPdfUrl = (id) => `${API}/semaforo/piano/${id}.pdf`;

// --- Dati LIVE (lo Studio in preview legge il sito pubblico) ---
export const getLiveStatus = () => api.get("/live/status").then((r) => r.data).catch(() => ({ live: false, base_url: null }));
export const liveGet = (path, params) => api.get(`/live/${path}`, { params }).then((r) => r.data);
export const livePost = (path) => api.post(`/live/${path}`).then((r) => r.data);
export const liveDelete = (path) => api.delete(`/live/${path}`).then((r) => r.data);
export const livePdfUrl = (base, id) => `${base}/api/semaforo/piano/${id}.pdf`;

export const CATEGORIES = ["Connessione", "Tabù di coppia", "Sistema"];
export const TIPI = ["Attenzione", "Educazione", "Autorevolezza", "Conversione"];

// --- Storie di oggi ---
export const getStorieDay = (date) => api.get("/storie/day", { params: date ? { date } : {}, timeout: 120000 }).then((r) => r.data);
export const regenStorieDay = (date) => api.post(`/storie/day/${date}/regenerate`, null, { timeout: 120000 }).then((r) => r.data);
export const storieDm = (date, testo) => api.post(`/storie/day/${date}/dm`, { testo }, { timeout: 90000 }).then((r) => r.data);
export const patchStorieSlot = (date, slotId, body) => api.patch(`/storie/day/${date}/slot/${slotId}`, body).then((r) => r.data);
export const getStorieHistory = (days = 14) => api.get("/storie/history", { params: { days } }).then((r) => r.data);
export const regenStorieSlot = (date, slotId) => api.post(`/storie/day/${date}/slot/${slotId}/regenerate`, null, { timeout: 90000 }).then((r) => r.data);

// --- Commenti (screenshot → testo, risposta, idea reel) ---
export const estraiCommenti = (payload) => api.post("/commenti/estrai", payload, { timeout: 120000 }).then((r) => r.data);
export const cesteAggiorna = (params = {}) => api.post("/ceste/aggiorna", null, { params }).then((r) => r.data);
export const cesteStato = () => api.get("/ceste/stato").then((r) => r.data);
export const cesteLista = () => api.get("/ceste").then((r) => r.data);
export const cesteUltimo = () => api.get("/ceste/ultimo").then((r) => r.data);
export const cesteReport = (id) => api.get(`/ceste/${id}`).then((r) => r.data);
export const listCommenti = (params = {}) => api.get("/commenti", { params }).then((r) => r.data);
export const patchCommento = (id, body) => api.patch(`/commenti/${id}`, body).then((r) => r.data);
export const rigeneraCommento = (id, nota) => api.post(`/commenti/${id}/rigenera`, { nota: nota || null }, { timeout: 90000 }).then((r) => r.data);
export const deleteCommento = (id) => api.delete(`/commenti/${id}`).then((r) => r.data);
